import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  DollarSign,
  Download,
  Server,
  Database,
  Bot,
  Clock,
  Shield,
  Loader2,
  ChevronDown,
  ChevronRight,
  Archive,
  Save,
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const RECURRING_META = {
  lightsail: { icon: Server, accent: 'text-teal-600', bg: 'bg-teal-50' },
  mongodb_atlas: { icon: Database, accent: 'text-emerald-600', bg: 'bg-emerald-50' },
  claude_subscription: { icon: Bot, accent: 'text-purple-600', bg: 'bg-purple-50' },
};

function formatUsd(amount) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
}

function formatDate(dateStr) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

export default function DevelopmentCostsPage() {
  const { user, authToken } = useAuth();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [savingKey, setSavingKey] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [showHistorical, setShowHistorical] = useState(false);
  const [historicalExpenses, setHistoricalExpenses] = useState(null);

  const userRole = user?.role;

  useEffect(() => {
    if (userRole && userRole !== 'SUPER_ADMIN') {
      toast.error('Access denied. Super Admin only.');
      navigate('/dashboard');
    }
  }, [userRole, navigate]);

  const fetchOverview = useCallback(async () => {
    if (!authToken) return;
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/api/admin/costs/overview`, {
        credentials: 'include',
        headers: { Authorization: `Bearer ${authToken}` },
      });
      if (res.status === 403) {
        toast.error('Access denied. Super Admin only.');
        navigate('/dashboard');
        return;
      }
      if (!res.ok) throw new Error('Failed to load cost overview');
      const data = await res.json();
      setOverview(data);
      // Seed draft inputs from the fetched recurring rates
      const nextDrafts = {};
      Object.entries(data.recurring || {}).forEach(([key, item]) => {
        nextDrafts[key] = item.pricing_model === 'hourly_capped_daily'
          ? {
              hourly_usd: item.hourly_usd?.toString() ?? '0',
              daily_cap_usd: item.daily_cap_usd?.toString() ?? '0',
              start_date: item.start_date ? item.start_date.slice(0, 10) : '',
            }
          : {
              monthly_usd: item.monthly_usd?.toString() ?? '0',
              start_date: item.start_date ? item.start_date.slice(0, 10) : '',
            };
      });
      setDrafts(nextDrafts);
    } catch (error) {
      console.error(error);
      toast.error('Failed to load cost data');
    } finally {
      setLoading(false);
    }
  }, [authToken, navigate]);

  useEffect(() => {
    if (userRole === 'SUPER_ADMIN' && authToken) fetchOverview();
  }, [userRole, authToken, fetchOverview]);

  const handleSaveRecurring = async (key) => {
    const draft = drafts[key];
    const isHourlyCapped = overview.recurring[key]?.pricing_model === 'hourly_capped_daily';
    setSavingKey(key);
    try {
      const payload = isHourlyCapped
        ? {
            hourly_usd: parseFloat(draft.hourly_usd) || 0,
            daily_cap_usd: parseFloat(draft.daily_cap_usd) || 0,
            start_date: draft.start_date ? new Date(draft.start_date).toISOString() : null,
          }
        : {
            monthly_cents: Math.round((parseFloat(draft.monthly_usd) || 0) * 100),
            start_date: draft.start_date ? new Date(draft.start_date).toISOString() : null,
          };
      const res = await fetch(`${API_URL}/api/admin/costs/recurring/${key}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error('Save failed');
      toast.success(`${RECURRING_META[key] ? overview.recurring[key].label : key} updated`);
      fetchOverview();
    } catch (error) {
      console.error(error);
      toast.error('Failed to save');
    } finally {
      setSavingKey(null);
    }
  };

  const toggleHistorical = async () => {
    if (showHistorical) {
      setShowHistorical(false);
      return;
    }
    setShowHistorical(true);
    if (historicalExpenses) return;
    try {
      const res = await fetch(`${API_URL}/api/admin/costs/prev-platform/expenses`, {
        credentials: 'include',
        headers: { Authorization: `Bearer ${authToken}` },
      });
      if (!res.ok) throw new Error('Failed to load historical entries');
      const data = await res.json();
      setHistoricalExpenses(data.expenses || []);
    } catch (error) {
      console.error(error);
      toast.error('Failed to load historical entries');
    }
  };

  const handleExportHistorical = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/costs/prev-platform/export`, {
        credentials: 'include',
        headers: { Authorization: `Bearer ${authToken}` },
      });
      if (!res.ok) throw new Error('Export failed');
      const data = await res.json();
      const blob = new Blob([data.csv_data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = data.filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success(`Exported ${data.expense_count} historical entries`);
    } catch (error) {
      console.error(error);
      toast.error('Failed to export');
    }
  };

  if (userRole && userRole !== 'SUPER_ADMIN') {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Shield className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-slate-900">Access Denied</h2>
          <p className="text-slate-600 mt-2">This page is restricted to Super Admin only.</p>
        </div>
      </div>
    );
  }

  if (loading || !overview) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-teal-600" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="development-costs-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Development Cost Tracker</h1>
        <p className="text-slate-600 mt-1">Total spend across the project's lifetime — hosting, tooling, and labor.</p>
      </div>

      {/* Grand Total Hero */}
      <Card className="bg-gradient-to-br from-slate-900 to-slate-800 text-white border-0">
        <CardContent className="p-6">
          <p className="text-slate-400 text-sm font-medium">TOTAL DEVELOPMENT SPEND</p>
          <p className="text-4xl font-bold mt-2" data-testid="grand-total-usd">
            {formatUsd(overview.grand_total_usd)}
          </p>
          <div className="flex flex-wrap gap-x-6 gap-y-1 mt-4 text-sm text-slate-300">
            <span>Prev. Platform: {formatUsd(overview.prev_platform_cost.total_usd)}</span>
            <span>Recurring: {formatUsd(overview.recurring_total_usd)}</span>
            <span>Labor: {formatUsd(overview.labor.subtotal_usd)}</span>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Prev. Platform Cost — frozen */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Archive className="h-5 w-5 text-slate-500" />
              Prev. Platform Cost
            </CardTitle>
            <CardDescription>Frozen rollup of the legacy prepaid credit ledger — no new entries.</CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold text-slate-900">{formatUsd(overview.prev_platform_cost.total_usd)}</p>
            <p className="text-xs text-slate-500 mt-1">{overview.prev_platform_cost.entry_count} historical transactions</p>
            <div className="flex gap-2 mt-3">
              <Button variant="outline" size="sm" onClick={toggleHistorical} data-testid="toggle-historical-btn">
                {showHistorical ? <ChevronDown className="h-4 w-4 mr-1" /> : <ChevronRight className="h-4 w-4 mr-1" />}
                {showHistorical ? 'Hide' : 'View'} historical entries
              </Button>
              <Button variant="outline" size="sm" onClick={handleExportHistorical} data-testid="export-historical-btn">
                <Download className="h-4 w-4 mr-1" />
                Export CSV
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Labor */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Clock className="h-5 w-5 text-amber-600" />
              Labor
            </CardTitle>
            <CardDescription>All conversation time on this project, regardless of platform or environment.</CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold text-slate-900">{formatUsd(overview.labor.subtotal_usd)}</p>
            <p className="text-xs text-slate-500 mt-1">
              {overview.labor.total_hours} hrs &times; {formatUsd(overview.labor.hourly_rate_usd)}/hr
            </p>
            <p className="text-xs text-slate-400 mt-2">
              As of {formatDate(overview.labor.computed_at)} &mdash; recomputed periodically via{' '}
              <code className="bg-slate-100 px-1 rounded">scripts/compute_labor_hours.py</code>, not live
              (the app has no access to local session logs).
            </p>
          </CardContent>
        </Card>

        {/* Recurring cost cards */}
        {Object.entries(overview.recurring).map(([key, item]) => {
          const meta = RECURRING_META[key] || { icon: DollarSign, accent: 'text-slate-600', bg: 'bg-slate-50' };
          const Icon = meta.icon;
          const isHourlyCapped = item.pricing_model === 'hourly_capped_daily';
          const draft = drafts[key] || (isHourlyCapped
            ? { hourly_usd: '0', daily_cap_usd: '0', start_date: '' }
            : { monthly_usd: '0', start_date: '' });
          return (
            <Card key={key} className="border-slate-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <Icon className={`h-5 w-5 ${meta.accent}`} />
                  {item.label}
                </CardTitle>
                <CardDescription>
                  {isHourlyCapped
                    ? <>{item.days_elapsed} days elapsed &times; {formatUsd(item.effective_daily_usd)}/day (capped)</>
                    : <>{item.months_elapsed} months elapsed &times; {formatUsd(item.monthly_usd)}/mo</>}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-bold text-slate-900 mb-3">{formatUsd(item.subtotal_usd)}</p>
                {isHourlyCapped ? (
                  <div className="grid grid-cols-3 gap-3">
                    <div className="space-y-1">
                      <Label htmlFor={`${key}-hourly`} className="text-xs">$/hour</Label>
                      <Input
                        id={`${key}-hourly`}
                        type="number"
                        min="0"
                        step="0.001"
                        value={draft.hourly_usd}
                        onChange={(e) => setDrafts((d) => ({ ...d, [key]: { ...d[key], hourly_usd: e.target.value } }))}
                        data-testid={`${key}-hourly-input`}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label htmlFor={`${key}-cap`} className="text-xs">Daily cap $</Label>
                      <Input
                        id={`${key}-cap`}
                        type="number"
                        min="0"
                        step="0.01"
                        value={draft.daily_cap_usd}
                        onChange={(e) => setDrafts((d) => ({ ...d, [key]: { ...d[key], daily_cap_usd: e.target.value } }))}
                        data-testid={`${key}-cap-input`}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label htmlFor={`${key}-start`} className="text-xs">Start date</Label>
                      <Input
                        id={`${key}-start`}
                        type="date"
                        value={draft.start_date}
                        onChange={(e) => setDrafts((d) => ({ ...d, [key]: { ...d[key], start_date: e.target.value } }))}
                        data-testid={`${key}-start-input`}
                      />
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label htmlFor={`${key}-monthly`} className="text-xs">$/month</Label>
                      <Input
                        id={`${key}-monthly`}
                        type="number"
                        min="0"
                        step="0.01"
                        value={draft.monthly_usd}
                        onChange={(e) => setDrafts((d) => ({ ...d, [key]: { ...d[key], monthly_usd: e.target.value } }))}
                        data-testid={`${key}-monthly-input`}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label htmlFor={`${key}-start`} className="text-xs">Start date</Label>
                      <Input
                        id={`${key}-start`}
                        type="date"
                        value={draft.start_date}
                        onChange={(e) => setDrafts((d) => ({ ...d, [key]: { ...d[key], start_date: e.target.value } }))}
                        data-testid={`${key}-start-input`}
                      />
                    </div>
                  </div>
                )}
                <Button
                  size="sm"
                  className="mt-3 bg-teal-600 hover:bg-teal-700"
                  onClick={() => handleSaveRecurring(key)}
                  disabled={savingKey === key}
                  data-testid={`${key}-save-btn`}
                >
                  {savingKey === key ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}
                  Save
                </Button>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Historical drill-down */}
      {showHistorical && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Prev. Platform Cost — historical entries</CardTitle>
            <CardDescription>Read-only. This ledger is closed; nothing writes new entries to it anymore.</CardDescription>
          </CardHeader>
          <CardContent>
            {!historicalExpenses ? (
              <div className="flex justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
              </div>
            ) : historicalExpenses.length === 0 ? (
              <p className="text-center py-8 text-slate-500">No historical entries.</p>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Vendor/Team</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                      <TableHead className="text-right">USD Equiv.</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {historicalExpenses.map((e, i) => (
                      <TableRow key={e.id || i}>
                        <TableCell>{formatDate(e.date)}</TableCell>
                        <TableCell>{e.vendor_team}</TableCell>
                        <TableCell className="text-right">
                          {e.currency === 'INR' ? '₹' : '$'}{e.amount?.toLocaleString()}
                        </TableCell>
                        <TableCell className="text-right text-slate-600">{formatUsd(e.amount_usd)}</TableCell>
                        <TableCell className="text-sm text-slate-600">{e.payment_status}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
